import { BiConformedKey } from '@euler/model/bi/catalog';
import { BiDatasetColumn, BiDatasetColumnRole } from '@euler/model/bi/dataset';
import {
  BiDisplayFormat,
  biDisplayFormatFitting,
} from '@euler/model/bi/format';
import { BiAggregation } from '@euler/model/bi/query';
import { BiFieldType } from '@euler/model/bi/result';
import { BiColumnOverride } from '@euler/model/bi/upload';

// The fit rules mirror backend `biRoleFits` / `biConformedKeyFits`: the
// server re-validates a confirmation and refuses a column that breaks them.

/** A measure is a number; a time is a date or datetime; a dimension any. */
export function biRoleFits(
  role: BiDatasetColumnRole,
  type: BiFieldType,
): boolean {
  if (role === BiDatasetColumnRole.Measure) return type === BiFieldType.Number;
  if (role === BiDatasetColumnRole.Time)
    return type === BiFieldType.Date || type === BiFieldType.Datetime;
  return true;
}

/** `date` sits on a date or datetime, every other key on text. */
export function biConformedKeyFits(
  key: BiConformedKey,
  type: BiFieldType,
): boolean {
  return key === BiConformedKey.Date
    ? type === BiFieldType.Date || type === BiFieldType.Datetime
    : type === BiFieldType.String;
}

/** One change the user makes to a column in the confirmation. */
export type BiColumnChange =
  | { field: 'label'; value: string }
  | { field: 'type'; value: BiFieldType }
  | { field: 'role'; value: BiDatasetColumnRole }
  | { field: 'format'; value: BiDisplayFormat | null }
  | { field: 'conformedKey'; value: BiConformedKey | null };

/**
 * The column after a change, kept valid: a type that no longer fits the
 * role or the conformed key moves the role to one that fits and drops the
 * key; a new measure sums by default; only a measure's format is sent.
 */
export function editBiColumn(
  column: BiDatasetColumn,
  change: BiColumnChange,
): BiDatasetColumn {
  const next: BiDatasetColumn = { ...column };
  switch (change.field) {
    case 'label':
      next.label = change.value;
      break;
    case 'type':
      next.type = change.value;
      if (!biRoleFits(next.role, next.type))
        next.role = biRoleFits(BiDatasetColumnRole.Time, next.type)
          ? BiDatasetColumnRole.Time
          : BiDatasetColumnRole.Dimension;
      if (
        next.conformedKey &&
        !biConformedKeyFits(next.conformedKey, next.type)
      )
        delete next.conformedKey;
      break;
    case 'role':
      if (biRoleFits(change.value, next.type)) next.role = change.value;
      break;
    case 'format':
      if (change.value) next.format = change.value;
      else delete next.format;
      break;
    case 'conformedKey':
      if (change.value === null) delete next.conformedKey;
      else if (biConformedKeyFits(change.value, next.type))
        next.conformedKey = change.value;
      break;
  }
  if (next.role === BiDatasetColumnRole.Measure)
    next.defaultAgg ??= BiAggregation.Sum;
  // A format stays while the column is not a measure (it is not sent),
  // so turning it back into one restores what the AI proposed.
  else delete next.defaultAgg;
  return next;
}

function sameFormat(
  a: BiDisplayFormat | undefined,
  b: BiDisplayFormat | undefined,
): boolean {
  const plain = (format: BiDisplayFormat | undefined) =>
    format
      ? JSON.stringify([
          format.kind,
          format.decimals,
          !!format.compact,
          format.unit,
          format.emptyAs,
        ])
      : '';
  return plain(a) === plain(b);
}

/** Whether every edited column can be sent: a label is required. */
export function biColumnsComplete(columns: BiDatasetColumn[]): boolean {
  return columns.every(column => !!column.label.trim());
}

/**
 * The confirmation's overrides: per column, only the fields the user
 * changed from what the AI understood.
 */
export function biColumnOverrides(
  original: BiDatasetColumn[],
  edited: BiDatasetColumn[],
): BiColumnOverride[] {
  return edited.flatMap(column => {
    const before = original.find(entry => entry.key === column.key);
    if (!before) return [];
    const override: BiColumnOverride = { key: column.key };
    const label = column.label.trim();
    if (label && label !== before.label) override.label = label;
    if (column.type !== before.type) override.type = column.type;
    if (column.role !== before.role) override.role = column.role;
    if (column.defaultAgg && column.defaultAgg !== before.defaultAgg)
      override.defaultAgg = column.defaultAgg;
    const measure = column.role === BiDatasetColumnRole.Measure;
    const format =
      measure && column.format
        ? biDisplayFormatFitting({
            ...column.format,
            unit: column.format.unit?.trim() || undefined,
          })
        : undefined;
    const was =
      before.role === BiDatasetColumnRole.Measure ? before.format : undefined;
    if (!sameFormat(format, was)) override.format = format ?? null;
    if (column.conformedKey !== before.conformedKey)
      override.conformedKey = column.conformedKey ?? null;
    return Object.keys(override).length > 1 ? [override] : [];
  });
}
